import { ChangeDetectionStrategy, Component, computed, inject, input, LOCALE_ID, signal } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { NgxEchartsModule } from 'ngx-echarts';
import { EChartsOption } from 'echarts';
import { SkeletonModule } from '@progress/kendo-angular-indicators';
import { DialogsModule } from '@progress/kendo-angular-dialog';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { NotificationModule } from '@progress/kendo-angular-notification';
import { SVGIconModule } from '@progress/kendo-angular-icons';
import { TooltipModule } from '@progress/kendo-angular-tooltip';
import { infoCircleIcon, moreVerticalIcon, SVGIcon } from '@progress/kendo-svg-icons';
import { CompositeFilterDescriptor, FilterDescriptor } from '@progress/kendo-data-query';
import DataTable from 'datatables.net-dt';
import { AnalyticsService, ChartData } from '../../../../../../../core/services/analytics.service';
import { DatatableComponent } from '../../../../../../../core/components/datatable/datatable.component';
import { DonutChartConfig } from '../../../../../../../core/charts/configs';
import { getAnalyticsColor } from '../../../../../../../core/charts/utils/chart-colors';
import { environment } from '../../../../../../../../environments/environment';
import { EdrCsvExportService } from '../../services/edr-csv-export.service';
import { formatDateRangeLabel } from '../../../../../../../core/shared/common/date-range.util';

/** Plain text cells: DataTables writes raw HTML, and render.text() is its own escaper. */
const text = DataTable.render.text();

@Component({
  selector: 'app-threat-status',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    NgxEchartsModule,
    SkeletonModule,
    DialogsModule,
    ButtonsModule,
    NotificationModule,
    SVGIconModule,
    TooltipModule,
    DatatableComponent,
  ],
  templateUrl: './threat-status.component.html',
  styleUrl: './threat-status.component.scss',
  providers: [AnalyticsService],
})
export class DetectedThreatStatusComponent {
  private csvExport = inject(EdrCsvExportService);

  private locale = inject(LOCALE_ID);

  /** Report type key for this view's CSV export. */
  private readonly reportKey = 'edr-threat-status-report';

  /** True while a CSV export is being queued. */
  public isExporting = signal<boolean>(false);

  public threatStatus = input<ChartData[]>([]);
  public organizationId = input<number | null>(null);
  public dateRange = input<string[] | null>(null);
  public isLoading = input<boolean>(false);

  public infoCircleIcon: SVGIcon = infoCircleIcon;
  public moreVerticalIcon: SVGIcon = moreVerticalIcon;

  public showPopup = signal<boolean>(false);

  public modalTitle = computed((): string => {
    const label = formatDateRangeLabel(this.dateRange());
    return label ? `Detected Threat Status (${label})` : 'Detected Threat Status';
  });

  public datatableUrl = computed((): string => {
    const id = this.organizationId();
    return id != null ? `${environment.apiUrl}/v1/analytics/administrative/edrthreatstatusdetails(${id})` : '';
  });

  public datatableColumns = [
    {
      data: 'timestamp',
      name: 'timestamp',
      title: 'Identified At',
      type: 'string',
      render: (value: string) => (value ? formatDate(value, 'MMM d, y, h:mm:ss a', this.locale) : ''),
    },
    {
      data: 'created_time',
      name: 'created_time',
      title: 'Reported At',
      type: 'string',
      render: (value: string) => (value ? formatDate(value, 'MMM d, y, h:mm:ss a', this.locale) : ''),
    },
    { data: 'threat_name', name: 'threat_name', title: 'Threat Name', type: 'string', render: text },
    { data: 'endpoint_name', name: 'endpoint_name', title: 'Device | Endpoint Name', type: 'string', render: text },
    { data: 'mitigation_status', name: 'mitigation_status', title: 'Status', type: 'string', render: text },
  ];

  public chartOptions = computed((): EChartsOption => {
    const data = this.threatStatus().map((item, index) => ({
      name: item.description || 'Unknown',
      value: item.count,
      color: item.color || getAnalyticsColor(index),
    }));
    return DonutChartConfig.create({ data, innerRadius: '50%', outerRadius: '75%' });
  });


  private filterDescriptor = computed((): CompositeFilterDescriptor => {
    const filters: FilterDescriptor[] = [];
    const range = this.dateRange();
    if (range) {
      filters.push({ field: 'timestamp', operator: 'gt', value: range[0] });
      filters.push({ field: 'timestamp', operator: 'lt', value: range[1] });
    }
    return { logic: 'and', filters };
  });


  public odataFilters = computed((): Record<string, string> => {
    const expression = this.csvExport.buildFilterExpression(this.filterDescriptor());
    return expression ? { $filter: encodeURIComponent(expression) } : {};
  });

  onViewData(): void {
    this.showPopup.set(true);
  }

  exportCsv(): void {
    this.isExporting.set(true);
    this.csvExport
      .export({
        reportKey: this.reportKey,
        organizationId: this.organizationId(),
        dateRange: this.dateRange(),
        filter: this.filterDescriptor(),
      })
      .subscribe({
        next: () => this.isExporting.set(false),
        error: () => this.isExporting.set(false),
      });
  }

  close(): void {
    this.showPopup.set(false);
  }
}
